import { Skeleton } from "@/components/Skeleton";
import type { BenchmarkDayPoint, BrokerBenchmarks } from "@/types";

interface Props {
  stats: BrokerBenchmarks | null;
  /** Header title — defaults to "Benchmarks". */
  title?: string;
}

/** Broker scorecard. Same component on Profile (broker's own view) and
 *  EmployeeDetail (manager+ view of a specific broker). Two headline
 *  metric blocks for the daily / monthly KPIs, then a current-month
 *  bar chart and won/lost split underneath for the colour to the
 *  manager's "how did the month go" question. */
export function BenchmarkCard({ stats, title = "Benchmarks" }: Props) {
  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          {title}
        </h3>
        {stats && (
          <span className="text-[10px] text-slate-600 tabular-nums">
            {fmtMonthRange(stats.month_start, stats.month_end)}
          </span>
        )}
      </div>

      {!stats ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
          <Skeleton className="h-[112px]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <HeadlineMetric
              label="Placements today"
              value={stats.placements_today}
              goal={stats.placement_goal_daily}
              hint="counted per platform — one source on 2 portals = 2"
            />
            <HeadlineMetric
              label="Deals this month"
              value={stats.deals_this_month}
              goal={stats.deal_goal_monthly}
              hint="closed deals — won or lost, both count"
            />
          </div>

          <MonthChart stats={stats} />

          <DealsBreakdown
            done={stats.deals_done}
            lost={stats.deals_lost}
            total={stats.deals_this_month}
            goal={stats.deal_goal_monthly}
          />
        </>
      )}
    </div>
  );
}

function HeadlineMetric({
  label,
  value,
  goal,
  hint,
}: {
  label: string;
  value: number;
  goal: number;
  hint?: string;
}) {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  const met = goal > 0 && value >= goal;
  const close = !met && pct >= 80;
  const barCls = met
    ? "bg-emerald-500"
    : close
      ? "bg-accent-500"
      : "bg-slate-500/60";
  const valueCls = met
    ? "text-emerald-300"
    : close
      ? "text-accent-200"
      : "text-slate-100";

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.1em] text-slate-500 font-semibold">
          {label}
        </span>
        {met ? (
          <span className="badge border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
            goal hit
          </span>
        ) : (
          <span className="text-[10px] text-slate-500 tabular-nums">{pct}%</span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5 mb-2.5">
        <span className={`text-3xl font-semibold tabular-nums leading-none ${valueCls}`}>
          {value}
        </span>
        <span className="text-[12px] text-slate-500 tabular-nums">
          / {goal}
        </span>
      </div>
      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${barCls} transition-all`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      {hint && (
        <div className="text-[10px] text-slate-600 mt-1.5 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}

/** Current-month per-day placement bar chart. Each bar covers ONE day
 *  in the month: empty bars are shown for days that haven't happened
 *  yet (so the chart width is stable on the 1st AND the 31st). The
 *  dashed line marks the agency goal; hit-days are green, missed are
 *  amber, future days are dim. */
function MonthChart({ stats }: { stats: BrokerBenchmarks }) {
  const goal = stats.placement_goal_daily;
  // Pad the trailing days of the month with placeholder slots so the
  // chart's x-axis stays a stable month-width: a broker reading their
  // scorecard on day 5 vs day 30 sees the same overall layout, just
  // more bars drawn in.
  const days: Array<BenchmarkDayPoint & { future?: boolean }> = [
    ...stats.placement_days,
  ];
  while (days.length < stats.placement_days_in_month) {
    days.push({
      date: "",
      count: 0,
      hit_goal: false,
      future: true,
    });
  }

  const maxCount = Math.max(
    goal,
    ...stats.placement_days.map((d) => d.count),
    1,
  );
  // Reserve 25% headroom above goal so the goal line never sits at the
  // ceiling and visually merges with a max bar.
  const yMax = Math.max(maxCount, Math.round(goal * 1.25));

  const todayIdx = stats.placement_days.length - 1;

  return (
    <div className="mt-4 pt-3 border-t border-white/5">
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Placements this month
          </span>
          <span className="text-[11px] text-slate-300 tabular-nums">
            {stats.placements_this_month}
          </span>
        </div>
        <div className="text-[10.5px] text-slate-500 tabular-nums">
          <span className="text-emerald-300 font-semibold">
            {stats.placement_days_hit}
          </span>
          <span className="text-slate-500">
            {" "}
            / {stats.placement_days_elapsed} days hit the {goal}-goal
          </span>
        </div>
      </div>

      <div className="relative h-[88px] flex items-end gap-[2px]">
        <div
          className="absolute left-0 right-0 border-t border-dashed border-rose-400/50 pointer-events-none"
          style={{ bottom: `${(goal / yMax) * 100}%` }}
          aria-hidden
        >
          <span className="absolute -top-3.5 right-0 text-[9px] font-semibold text-rose-300/80 bg-ink-900/70 px-1 rounded">
            goal {goal}
          </span>
        </div>

        {days.map((p, i) => {
          if (p.future) {
            return (
              <div
                key={`f${i}`}
                className="flex-1 h-full flex items-end"
                title={`day ${i + 1}`}
              >
                <div className="w-full bg-white/[0.025] rounded-t-[2px] h-[2px]" />
              </div>
            );
          }
          const heightPct = (p.count / yMax) * 100;
          const isToday = i === todayIdx;
          const tone = p.hit_goal
            ? "bg-emerald-500/80 hover:bg-emerald-400"
            : p.count > 0
              ? "bg-accent-500/65 hover:bg-accent-400"
              : "bg-white/[0.06] hover:bg-white/[0.12]";
          return (
            <div
              key={p.date}
              className="flex-1 h-full flex items-end relative group/bar"
              title={`${fmtDay(p.date)} · ${p.count}/${goal}`}
            >
              <div
                className={`w-full rounded-t-[2px] transition-colors ${tone} ${
                  isToday ? "ring-1 ring-accent-300/70" : ""
                }`}
                style={{
                  height: `${Math.max(heightPct, p.count > 0 ? 3 : 1)}%`,
                }}
              />
              <div className="opacity-0 group-hover/bar:opacity-100 transition pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-900/95 border border-white/10 text-slate-200 whitespace-nowrap z-10">
                {fmtDay(p.date)} · {p.count}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between mt-1.5 text-[9.5px] text-slate-600 tabular-nums">
        <span>{fmtDay(stats.month_start)}</span>
        <span>
          today · day {stats.placement_days_elapsed} / {stats.placement_days_in_month}
        </span>
        <span>{fmtDay(stats.month_end)}</span>
      </div>
    </div>
  );
}

function DealsBreakdown({
  done,
  lost,
  total,
  goal,
}: {
  done: number;
  lost: number;
  total: number;
  goal: number;
}) {
  if (total === 0 && goal === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-white/5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Deals closed this month
          </span>
          <span className="text-[11px] text-slate-300 tabular-nums">
            {total}
            <span className="text-slate-600"> / {goal}</span>
          </span>
        </div>
        <div className="flex items-baseline gap-3 text-[11px] tabular-nums">
          <span>
            <span className="text-emerald-300">✓ {done}</span>
            <span className="text-slate-500"> done</span>
          </span>
          <span>
            <span className="text-rose-300/80">✗ {lost}</span>
            <span className="text-slate-500"> lost</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function fmtMonthRange(startIso: string, endIso: string): string {
  const fmt = (s: string) => {
    const d = new Date(`${s}T00:00:00`);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return `${fmt(startIso)} – ${fmt(endIso)}`;
}

function fmtDay(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
